<template>
    <transition
        name="auth"
        mode="out-in"
    >
        <RegistrationForm
            v-if="isRegistration"
            :errors="registrationErrors"
            :disabled="registrationDisabled"
            :hide-inputs-if-logged="registrationHideInputsIfLogged"
            :back-button-in-title
            @submit="$emit('register', $event)"
            @switch-to-login="switchToLogin"
        >
            <template #title>
                <slot name="registration-title" />
            </template>

            <template #description>
                <slot name="registration-description" />
            </template>

            <template #contact>
                <slot name="registration-contact" />
            </template>

            <template #submit>
                <slot name="registration-submit" />
            </template>
        </RegistrationForm>

        <LoginForm
            v-else
            :back-button-in-title
            @login="$emit('login', $event)"
            @error="$emit('loginError', $event)"
            @switch-to-registration="switchToRegistration"
        >
            <template #title>
                <slot name="login-title" />
            </template>
        </LoginForm>
    </transition>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import type { ErrorCollectionWithTraces } from '@/helpers/errors'

import LoginForm from './LoginForm.vue'
import RegistrationForm from './RegistrationForm.vue'
import type {
    LoginResponseFailureFragment,
    LoginResponseSuccessFragment,
} from '@/graphql/api'

const { startWithRegistration = true } = defineProps<{
    startWithRegistration?: boolean
    registrationErrors: ErrorCollectionWithTraces
    registrationDisabled?: boolean
    registrationHideInputsIfLogged?: boolean
    backButtonInTitle?: boolean
}>()

defineEmits<{
    login: [v: LoginResponseSuccessFragment]
    register: [successCallback: () => void]
    loginError: [v: LoginResponseFailureFragment]
}>()

const isRegistration = ref(startWithRegistration)
const switchToLogin = () => {
    isRegistration.value = false
}
const switchToRegistration = () => {
    isRegistration.value = true
}

defineExpose({
    switchToLogin,
    switchToRegistration,
})
</script>

<style scoped>
.auth-enter-active,
.auth-leave-active {
    transition: opacity 0.45s ease;
}

.auth-enter-from,
.auth-leave-to {
    opacity: 0;
}
</style>
